<template>
  <!-- One line: the count, then a compact tab per meter; scrolls sideways when there are many -->
  <div class="tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto" role="tablist">
    <span
      class="tw-flex-shrink-0 tw-text-xs tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
      :title="replacedCount ? `${replacedCount} vervangen` : ''"
    >
      {{ currentCount === 1 ? '1 meter' : `${currentCount} meters` }}
    </span>
    <span
      v-if="isLoading"
      class="tw-flex-shrink-0 tw-w-3 tw-h-3 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin"
    />
    <button
      v-for="device in devices"
      :key="device.serial_number"
      type="button"
      role="tab"
      :aria-selected="device.serial_number === selected"
      class="tw-flex-shrink-0 tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-2 tw-py-1 tw-text-xs tw-whitespace-nowrap tw-transition-colors"
      :class="tabClass(device)"
      :title="tabTitle(device)"
      @click="$emit('select', device.serial_number)"
    >
      <span class="tw-w-2 tw-h-2 tw-rounded-full tw-flex-shrink-0" :class="dotClass(device)" />
      <!-- the source only when the meters differ in it (or a switched meter): mostly they are all the same -->
      <span v-if="showSources(device)" class="tw-flex tw-items-center tw-gap-1">
        <template v-for="(source, i) in tabSources(device)">
          <span v-if="i > 0" :key="`arrow-${source}`" class="tw-text-gray-400">→</span>
          <meter-source-badge :key="source" :meter="{ sources: [source] }" small />
        </template>
      </span>
      <span class="tw-font-mono tw-font-medium">{{ device.serial_number }}</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatDateNL } from './formatters'
import MeterSourceBadge from './MeterSourceBadge.vue'

// The meters and inverters at the address as a bar of tabs in the overview section (from useAddressDevices):
// current first, replaced ones greyed out; status and period in the tooltip.
const props = defineProps({
  devices: { type: Array, required: true },
  selected: { type: String, required: true },
  isLoading: { type: Boolean, default: false },
})

defineEmits(['select'])

const currentCount = computed(() => props.devices.filter((d) => d.current).length)
const replacedCount = computed(() => props.devices.length - currentCount.value)

// all meters read the same way: the badge says nothing, leave it out
const sourcesDiffer = computed(() =>
  new Set(props.devices.map((d) => tabSources(d).join('>'))).size > 1
)
const showSources = (device) => sourcesDiffer.value || tabSources(device).length > 1

// a switched meter shows its history: Cast4all -> SolarEdge
const tabSources = (device) => {
  const fromHistory = [...new Set((device.history ?? []).map((p) => p.source).filter(Boolean))]
  return fromHistory.length > 1 ? fromHistory : device.sources
}

const tabClass = (device) => {
  if (device.serial_number === props.selected) return 'tw-border-blue-400 tw-bg-blue-50 tw-text-gray-800'
  if (!device.current) return 'tw-border-gray-200 tw-bg-gray-50 tw-text-gray-400 hover:tw-border-gray-300'
  return 'tw-border-gray-200 tw-bg-white tw-text-gray-700 hover:tw-border-blue-400 hover:tw-text-blue-600'
}

const dotClass = (device) => {
  if (!device.current) return 'tw-bg-gray-300'
  if (device.alive === true) return 'tw-bg-green-500'
  if (device.alive === false) return 'tw-bg-red-500'
  return 'tw-bg-gray-300'
}

// status and period on hover; the meter section shows the rest once picked
const tabTitle = (device) => {
  const from = device.valid_from ? formatDateNL(device.valid_from) : null
  if (!device.current) {
    const to = device.valid_to ? formatDateNL(device.valid_to) : '…'
    return `Vervangen: ${from ?? '…'} t/m ${to}${device.alive ? ', communiceert nog' : ''}`
  }
  const status = device.alive === true ? 'Online' : device.alive === false ? 'Offline' : 'Status onbekend'
  return from ? `${status}, sinds ${from}` : status
}
</script>
